import { useForm, router } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { FormSection } from '@/components/backoffice/FormSection'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import type { JurisdictionOptions } from '@/types'

type Props = { jurisdiction_options: JurisdictionOptions }

export default function NomineeDirectorTypesNew({ jurisdiction_options }: Props) {
  const { data, setData, processing, errors } = useForm({
    name: '', description: '',
    country_id: '', state_id: '', local_area_id: '',
    gov_fee_cents: 0, service_fee_cents: 50000,
    min_duration_months: 12,
    processing_days_min: 3, processing_days_max: 7,
  })

  const selectClass = "flex h-9 w-full items-center rounded-md border border-input bg-transparent pl-3 pr-8 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    router.post('/backoffice/nominee_director_types', { nominee_director_type: data })
  }

  return (
    <BackofficeLayout title="New Nominee Director Type" breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Nominee Director Types', href: '/backoffice/nominee_director_types' }, { label: 'New Type' }]}>
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">New Nominee Director Type</h1>
        </div>

        <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
          <FormSection title="Identification">
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Name</label>
              <Input className="h-8 text-sm" value={data.name} onChange={e => setData('name', e.target.value)} placeholder="e.g., Delaware Nominee Director" />
              {errors.name && <p className="text-[12px] text-destructive">{errors.name}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Description</label>
              <Textarea className="text-sm min-h-15" value={data.description} onChange={e => setData('description', e.target.value)} />
            </div>
          </FormSection>

          <FormSection title="Jurisdiction">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Country</label>
                <select value={data.country_id} onChange={e => setData(d => ({ ...d, country_id: e.target.value, state_id: '', local_area_id: '' }))} className={selectClass}>
                  <option value="">Select...</option>
                  {jurisdiction_options.countries.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">State</label>
                <select value={data.state_id} onChange={e => setData(d => ({ ...d, state_id: e.target.value, local_area_id: '' }))} className={selectClass} disabled={!data.country_id}>
                  <option value="">Select...</option>
                  {jurisdiction_options.states.filter(s => s.country_id === data.country_id).map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Local Area</label>
                <select value={data.local_area_id} onChange={e => setData('local_area_id', e.target.value)} className={selectClass} disabled={!data.state_id}>
                  <option value="">None</option>
                  {jurisdiction_options.local_areas.filter(a => a.state_id === data.state_id).map(a => <option key={a.id} value={a.id}>{a.name} ({a.code})</option>)}
                </select>
              </div>
            </div>
          </FormSection>

          <FormSection title="Fees & Duration">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Gov Fee (cents)</label>
                <Input className="h-8 text-sm" type="number" value={data.gov_fee_cents} onChange={e => setData('gov_fee_cents', parseInt(e.target.value) || 0)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Service Fee (cents)</label>
                <Input className="h-8 text-sm" type="number" value={data.service_fee_cents} onChange={e => setData('service_fee_cents', parseInt(e.target.value) || 0)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Minimum Duration (months)</label>
              <Input className="h-8 text-sm" type="number" value={data.min_duration_months} onChange={e => setData('min_duration_months', parseInt(e.target.value) || 12)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Processing Days (min)</label>
                <Input className="h-8 text-sm" type="number" value={data.processing_days_min} onChange={e => setData('processing_days_min', parseInt(e.target.value) || 0)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Processing Days (max)</label>
                <Input className="h-8 text-sm" type="number" value={data.processing_days_max} onChange={e => setData('processing_days_max', parseInt(e.target.value) || 0)} />
              </div>
            </div>
          </FormSection>

          <div className="flex gap-2 pt-2">
            <Button type="submit" size="sm" disabled={processing} className="h-8 text-[13px]">Create Type</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => window.history.back()} className="h-8 text-[13px]">Cancel</Button>
          </div>
        </form>
      </div>
    </BackofficeLayout>
  )
}
